import { useCallback, useEffect, useState } from "react";
import type { ReportingOptions } from "../../../types/stationPerformance";
import { DEFAULT_OPTIONS, OPTIONS_STORAGE_KEY } from "../StationPerformance.consts";

/**
 * The reporting options, persisted across tabs and reloads (FR-030a).
 *
 * localStorage rather than a context, per the codebase convention that contexts are for truly global
 * state only. Persisting matters here beyond convenience: switching screen must not silently change the
 * lens a comparison was started under.
 */
export const useReportingOptions = () => {
  const [options, setOptions] = useState<ReportingOptions>(() => {
    try {
      const stored = localStorage.getItem(OPTIONS_STORAGE_KEY);
      if (!stored) return DEFAULT_OPTIONS;
      // Merge over the defaults so a stored object written by an older version, missing a field, cannot
      // leave an option undefined.
      return { ...DEFAULT_OPTIONS, ...JSON.parse(stored) };
    } catch (error) {
      console.error("Could not read stored reporting options, using defaults:", error);
      return DEFAULT_OPTIONS;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(OPTIONS_STORAGE_KEY, JSON.stringify(options));
    } catch (error) {
      console.error("Could not persist reporting options:", error);
    }
  }, [options]);

  const resetOptions = useCallback(() => setOptions(DEFAULT_OPTIONS), []);

  return { options, setOptions, resetOptions };
};
